import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import type { Order } from '@/types'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Date format mappings (format pattern -> Intl.DateTimeFormat options)
const DATE_FORMAT_MAP: Record<string, Intl.DateTimeFormatOptions> = {
  'YYYY-MM-DD': { year: 'numeric', month: '2-digit', day: '2-digit' },
  'MM/DD/YYYY': { year: 'numeric', month: '2-digit', day: '2-digit' },
  'DD/MM/YYYY': { year: 'numeric', month: '2-digit', day: '2-digit' },
  'DD-MM-YYYY': { year: 'numeric', month: '2-digit', day: '2-digit' },
  'YYYY/MM/DD': { year: 'numeric', month: '2-digit', day: '2-digit' },
}

// Format date string according to format pattern
function formatDateString(date: Date, formatPattern: string): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  
  switch (formatPattern) {
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`
    case 'DD/MM/YYYY':
      return `${day}/${month}/${year}`
    case 'DD-MM-YYYY':
      return `${day}-${month}-${year}`
    case 'YYYY/MM/DD':
      return `${year}/${month}/${day}`
    default:
      return `${year}-${month}-${day}` // Default to ISO
  }
}

/** Coerce API / form values (often decimal strings) to a finite number. */
export function parseAmount(value: unknown): number {
  if (value === null || value === undefined || value === '') return 0
  const n = typeof value === 'number' ? value : parseFloat(String(value))
  return Number.isFinite(n) ? n : 0
}

/** Join first/last name; omits null, undefined, and blank parts (avoids "First null" in template strings). */
export function formatPersonName(
  first?: string | null,
  last?: string | null,
  fallback = '',
): string {
  const name = [first, last]
    .map((part) => (part == null ? '' : String(part).trim()))
    .filter(Boolean)
    .join(' ')
  return name || fallback
}

/** Sum monetary values without string concatenation (avoids NaN in totals). */
export function sumAmounts(amounts: Array<unknown>): number {
  return amounts.reduce((sum: number, item) => sum + parseAmount(item), 0)
}

/** Remaining balance: order final_amount minus sum of order_payments allocations. */
export function orderOutstandingBalance(
  order: Pick<Order, 'final_amount' | 'payments'>
): number {
  const finalAmt = parseAmount(order.final_amount)
  const paid = (order.payments ?? []).reduce(
    (sum, p) => sum + parseAmount(p.allocated_amount),
    0
  )
  return Math.max(0, Math.round((finalAmt - paid) * 100) / 100)
}

export function formatCurrency(
  amount: number | string,
  currency?: string | null,
  locale: string = 'en-US'
): string {
  const value = parseAmount(amount)
  const code = String(currency ?? '')
    .trim()
    .toUpperCase()
  if (code.length >= 3) {
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: code.slice(0, 10),
      }).format(value)
    } catch {
      // fall through to plain number
    }
  }
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

/** Extract YYYY-MM-DD from API date, datetime, or locale date strings. */
export function normalizeApiDateKey(value: string | Date | undefined | null): string {
  if (value == null || value === '') return ''
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return ''
    return toLocalDateKey(value)
  }
  const s = String(value).trim()
  const iso = s.match(/^(\d{4}-\d{2}-\d{2})/)
  if (iso) return iso[1]!
  const parsed = new Date(s)
  if (!Number.isNaN(parsed.getTime())) return toLocalDateKey(parsed)
  return ''
}

export function formatDate(
  date: string | Date, 
  dateFormat?: string,
  options?: Intl.DateTimeFormatOptions
): string {
  const dateObj =
    typeof date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(date.trim())
      ? parseLocalDateFromApi(date)
      : typeof date === 'string'
        ? new Date(date)
        : date

  if (Number.isNaN(dateObj.getTime())) {
    return '—'
  }
  
  // If custom date format is provided, use it
  if (dateFormat && DATE_FORMAT_MAP[dateFormat]) {
    return formatDateString(dateObj, dateFormat)
  }
  
  // Otherwise use Intl.DateTimeFormat with options
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  }).format(dateObj)
}

/** YYYY-MM-DD in the local calendar (for API date-only fields). */
export function toLocalDateKey(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** Parse API date/datetime to a local Date at midnight (for date pickers). */
export function parseLocalDateFromApi(value: string | Date): Date {
  if (value instanceof Date) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate())
  }
  const key = String(value).slice(0, 10)
  const [y, m, d] = key.split('-').map(Number)
  return new Date(y, m - 1, d)
}

export function formatDateTime(date: string | Date): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date

  if (Number.isNaN(dateObj.getTime())) {
    return '—'
  }

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(dateObj)
}

export function formatNumber(number: number): string {
  return new Intl.NumberFormat('en-US').format(number)
}

/**
 * Normalize barcode that may be in scientific notation (e.g. "2.52571E+11" from Excel)
 * to full digit string (e.g. "252571000000"). Barcodes should always display as plain digits.
 */
export function normalizeBarcode(barcode: string | number | undefined | null): string {
  if (barcode == null || barcode === '') return '';
  const str = String(barcode).trim();
  if (!str) return '';
  // Scientific notation: 2.52571E+11, 1.23e-5, etc.
  const sciMatch = str.match(/^(\d+\.?\d*)[eE]([+-]?\d+)$/);
  if (sciMatch) {
    const num = parseFloat(str);
    return Number.isFinite(num) ? String(Math.round(num)) : str;
  }
  return str;
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength) + '...'
}

export function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export function generateId(): string {
  return Math.random().toString(36).substr(2, 9)
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: number
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func(...args), delay) as any
  }
}

export function throttle<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let lastCall = 0
  return (...args: Parameters<T>) => {
    const now = Date.now()
    if (now - lastCall >= delay) {
      lastCall = now
      func(...args)
    }
  }
}

/**
 * Format order payment status for display.
 * Any linked payment in `pending` (manual verification) → "Processing".
 * Otherwise: Paid / Partially Paid / Unpaid from completed allocations.
 */
export function formatOrderPaymentStatus(
  paymentStatus: string,
  paymentWorkflowStatus?: string | null
): string {
  const statusLabels: Record<string, string> = {
    paid: 'Paid',
    unpaid: 'Unpaid',
    partially_paid: 'Partially Paid',
    refunded: 'Refunded',
    partially_refunded: 'Partially Refunded',
  };

  if (paymentWorkflowStatus === 'processing') {
    return 'Processing';
  }

  return statusLabels[paymentStatus] || paymentStatus.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function getStatusColor(status: string): string {
  const statusColors: Record<string, string> = {
    active: 'status-active',
    inactive: 'status-inactive',
    pending: 'status-pending',
    confirmed: 'status-pending',
    processing: 'status-pending',
    shipped: 'status-pending',
    delivered: 'status-completed',
    completed: 'status-completed',
    cancelled: 'status-cancelled',
    refunded: 'status-cancelled',
    paid: 'status-completed',
    unpaid: 'status-pending',
    partially_paid: 'status-pending',
    partially_refunded: 'status-pending',
    failed: 'status-cancelled',
    suspended: 'status-cancelled',
  }
  
  return statusColors[status] || 'badge-secondary'
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map(word => word.charAt(0).toUpperCase())
    .join('')
    .slice(0, 2)
}

export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

export function isValidPhone(phone: string): boolean {
  const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/
  return phoneRegex.test(phone.replace(/\s/g, ''))
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

export function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard) {
    return navigator.clipboard.writeText(text)
  } else {
    // Fallback for older browsers
    const textArea = document.createElement('textarea')
    textArea.value = text
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()
    try {
      document.execCommand('copy')
    } catch (err) {
      console.error('Failed to copy text: ', err)
    }
    document.body.removeChild(textArea)
    return Promise.resolve()
  }
}

export function parseError(error: any): string {
  if (typeof error === 'string') return error
  if (error?.message) return error.message
  if (error?.response?.data?.message) return error.response.data.message
  return 'An unexpected error occurred'
}

export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

// Auth utilities
export function getAuthToken(): string | null {
  const state = JSON.parse(localStorage.getItem('auth-storage') || '{}')
  return state.state?.token || null
}